import * as Sentry from "@sentry/react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { LoaderLogo } from "components/Loader/logo";
import { env } from "config/env";
import { messagingViewTransition } from "domains/messaging/slideTransition";
import { PersistedQueryClientProvider, queryClient } from "providers/PersistedQueryClient";
import React from "react";
import { hardenSearchValidation } from "./resilientSearch";
import { adminRoutes, settingsRoutes } from "./tree/admin";
import { authedRoute } from "./tree/authed";
import { clockKioskRoute } from "./tree/clockKiosk";
import { connectLimitedRoute, connectLocationRoutes } from "./tree/connect";
import { hireRoutes } from "./tree/hire";
import { locationLayoutRoute, locationsListRoute, rootIndexRoute } from "./tree/locations";
import { marketplaceRoutes } from "./tree/marketplace";
import { messagingInboxRoutes, messagingStandaloneRoutes } from "./tree/messaging";
import { onboardingAuthedRoutes, onboardingRootRoutes } from "./tree/onboarding";
import { locationSettingsRoutes, locationsListRoutes, orgDetailsRoute } from "./tree/orgs";
import { rootRoute } from "./tree/root";
import { signedUpFallbackRoute, signedUpRoute } from "./tree/signedUp";

const routeTree = rootRoute.addChildren([
  authedRoute.addChildren([
    signedUpRoute.addChildren([
      rootIndexRoute,
      locationsListRoute.addChildren(locationsListRoutes),
      locationLayoutRoute.addChildren([
        ...locationSettingsRoutes,
        hireRoutes,
        marketplaceRoutes,
        connectLocationRoutes,
        messagingInboxRoutes,
      ]),
      orgDetailsRoute,
      adminRoutes,
      ...settingsRoutes,
      signedUpFallbackRoute,
    ]),
    connectLimitedRoute,
    ...onboardingAuthedRoutes,
  ]),
  messagingStandaloneRoutes,
  clockKioskRoute,
  ...onboardingRootRoutes,
]);

hardenSearchValidation(routeTree);

const TRAILING_SLASHES = /\/+$/;

const router = createRouter({
  routeTree,
  // Canonicalize a trailing slash off the parsed pathname so pathname-equality and matchRoute checks
  // (tab selection in ManageLocation, Connect, dashboards, …) match. TanStack's `trailingSlash` option
  // only normalizes generated links, not a directly-loaded URL.
  rewrite: {
    input: ({ url }) => {
      if (url.pathname.length > 1) url.pathname = url.pathname.replace(TRAILING_SLASHES, "");
      return url;
    },
  },
  context: { queryClient },
  defaultPreload: "intent",
  // RQ owns freshness; the router shouldn't second-guess loader/query staleness.
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: LoaderLogo,
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
});

router.update({
  ...router.options,
  defaultViewTransition: messagingViewTransition((location) => router.matchRoutes(location.pathname, location.search)),
});

// tanstackRouterBrowserTracingIntegration needs the router instance, which doesn't exist when
// instrument.ts runs Sentry.init (caveat #2), so it's attached here after creation.
Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Read by the e2e route-coverage gate in tests/views/routesSmoke.spec.ts. CI's e2e build is a
// production `vite build`, so this can't gate on import.meta.env.DEV.
if (env.IS_LOCAL || env.IS_AUTOMATION) {
  Object.assign(globalThis, { __appRouter: router });
}

export function Router() {
  return (
    <React.Suspense fallback={<LoaderLogo />}>
      <PersistedQueryClientProvider>
        <RouterProvider router={router} />
      </PersistedQueryClientProvider>
    </React.Suspense>
  );
}
